<template>
	<DashCard
		title="Leads Overview"
		:loading="loading"
		:has-error="hasError"
		:no-data="noLoadedData"
		:info-data="infoData"
		@reload="$emit('reload', 'leads_overview')"
	>
		<template #icon><LeadsOverviewIcon /></template>

		<div class="card-content">
			<v-data-table
				:headers="headers"
				:items="computedData"
				item-key="level"
				mobile-breakpoint="0"
				class="card-table"
				dark hide-default-footer disable-sort
			>
				<template #item.level="{ item }">
					<span><strong>{{ item.level }}</strong></span>
				</template>
				<template #item.suggested="{ item }">
					<span>{{ formatItem(item.suggested) }}</span>
				</template>
				<template #item.purchase="{ item }">
					<span class="nowrap font-weight-bold">
						<CalculationDot :actual="item.purchase" :suggested="item.suggested" />
						<span>{{ formatItem(item.purchase) }}</span>
					</span>
				</template>
				<template #item.cost="{ item }">
					<span class="with-currency">{{ formatMoney(item.cost) }}</span>
				</template>
				<template #item.closeRate="{ item }">
					<span class="nowrap">{{ formatRate(item.closeRate) }}%</span>
				</template>
				<template #item.topPerformerCloseRate="{ item }">
					<span class="nowrap">{{ formatRate(item.topPerformerCloseRate) }}%</span>
				</template>
			</v-data-table>
		</div>
	</DashCard>
</template>

<script>
import DashCard from '../DashCard.vue'
import LeadsOverviewIcon from '../../Icons/LeadsOverviewIcon.vue'
import CalculationDot from '../../Components/CalculationDot.vue'

const getLeadsOverview = (period, data, suggested, getCR, calculateSL) => {
	return data.map((item) => ({
		level: item.lead_level,
		purchase: item[`lead_purchase_${period}`],
		cost: item[`lead_cost_${period}`],
		closeRate: item.close_ratio,
		topPerformerCloseRate: getCR(item.lead_level, suggested),
		suggested: calculateSL(item.lead_level, suggested)
	})).sort((a, b) => a.level.localeCompare(b.level))
}

const emptyData = [
	{ lead_level: 'A' },
	{ lead_level: 'B' },
	{ lead_level: 'DL' },
	{ lead_level: 'R' },
]

export default {
    components: {
		DashCard,
		LeadsOverviewIcon,
		CalculationDot,
	},
	props: {
		data: {
			type: Object,
			default: null,
		},
		monthlyPeriod: {
			type: Boolean,
			default: true,
		},
		infoData: Object,
	},
    computed: {
		headers () {
			return [{
                text: 'Lead Level',
                value: 'level',
            }, {
                text: 'Suggested Leads',
                value: 'suggested',
			}, {
                text: 'Lead Purchase',
                value: 'purchase',
            }, {
                text: 'Lead Cost',
                value: 'cost',
            }, {
                text: 'Close Rate',
                value: 'closeRate',
            }, {
                text: 'Top\u00A0Performer Close\u00A0Rate',
                value: 'topPerformerCloseRate',
            }]
		},
		loading () {
			if (!this.data) { return false }
			return this.data.loading
		},
		hasError () {
			if (!this.data) { return false }
			return this.data.error
		},
		noLoadedData () {
			if (this.data && this.data.loaded) {
				if (!this.data.data) { return true }
				return this.data.data.every(item => Object.values(item).every(this.checkNullish))
			}
			return false
		},
		computedData () {
			let data = emptyData
			if (this.data && this.data.loaded && !this.noLoadedData) {
				data = this.data.data
			}
			const monthly = getLeadsOverview('monthly', data, this.data.suggested, this.getCloseRate, this.calculateSuggestedLeads)
			const weekly = getLeadsOverview('weekly', data, this.data.suggested, this.getCloseRate, this.calculateSuggestedLeads)
			return this.monthlyPeriod ? monthly : weekly
		},
    },
	methods: {
		checkNullish (value) {
			return [undefined, null, ''].includes(value)
		},
		getCloseRate(level = '', suggested) {
			if (!suggested) { return undefined }
			return suggested[`close_ratio_${level.toLowerCase()}`] ?? undefined
		},
		getAverageAPV(level = '', suggested) {
			if (!suggested) { return undefined }
			return suggested[`apv_average_${level.toLowerCase()}`] ?? undefined
		},
		calculateSuggestedLeads (level = '', suggested) {
			const closeRate = this.getCloseRate(level, suggested)
			const averageAPV = this.getAverageAPV(level, suggested)
			const hasNullish = [suggested?.neededSubmit, closeRate, averageAPV].some(this.checkNullish)
			if (hasNullish) { return undefined }
			if (averageAPV * closeRate === 0) { return undefined }
			return Math.ceil((suggested.neededSubmit / 4) / (averageAPV * closeRate))
		},
		formatMoney (value) {
			if (this.checkNullish(value)) { return 'NA' }
			return this.formatNumber(value, '(0,0.00)')
		},
		formatRate (value) {
			if (this.checkNullish(value)) { return 'NA' }
			return this.formatNumber(100 * value, '(0)')
		},
		formatItem (value) {
			if (this.checkNullish(value)) { return 'NA' }
			return value
		},
	},
}
</script>

<style lang="scss" scoped>
@import '../../style/index.scss';

$block: card-content;
$scrollbar-width-chrome: 8px;

.#{$block} {
	color: $color-text;

	.card-table {
		$border-bottom: 1px solid $color-border !important;

		background-color: transparent;
		color: inherit;
		border-radius: 0;

		&::v-deep .v-data-table-header tr th {
			border-bottom: $border-bottom;
			padding-left: 10px;
			padding-right: 10px;
			height: 60px;
			span {
				display: inline-block;
				color: $color-text;
				line-height: 1.5;
				font-weight: bold;
				max-width: min-content;
			}
		}

		&::v-deep .v-data-table__wrapper {
			&::-webkit-scrollbar {
				width: $scrollbar-width-chrome;
				height: $scrollbar-width-chrome;
			}

			&::-webkit-scrollbar-track {
				box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.25);
				border-radius: 2 * $scrollbar-width-chrome;
			}

			&::-webkit-scrollbar-thumb {
				background-color: $color-divider;
				border-radius: 0.5 * $scrollbar-width-chrome;
			}

			&::-webkit-scrollbar-corner {
				background: transparent;
			}

			scrollbar-color: $color-divider transparent;
			scrollbar-width: thin;
		}

		&::v-deep tbody tr td {
			height: 36px;
			border-bottom: $border-bottom;

			&:first-child {
				background-color: $color-water-green-20;
				text-align: center !important;
			}
		}
	}
}
</style>
